import { addDays, isDayKey, parseHm, weekdayOfKey, type DayKey } from "../time.js";
import { normalizeText } from "../rules.js";

const WEEKDAYS: [RegExp, number][] = [
  [/niedziel/, 0],
  [/poniedzia/, 1],
  [/wtor/, 2],
  [/środ|srod/, 3],
  [/czwart/, 4],
  [/piąt|piat/, 5],
  [/sobot/, 6],
];

const NUMBER_WORDS: Record<string, number> = {
  jeden: 1,
  jedną: 1,
  dwa: 2,
  dwie: 2,
  trzy: 3,
  cztery: 4,
  pięć: 5,
  piec: 5,
  sześć: 6,
  siedem: 7,
  osiem: 8,
  dziewięć: 9,
  dziesięć: 10,
  kilka: 3,
  parę: 3,
  pare: 3,
};

function nextWeekday(today: DayKey, wd: number, includeToday = false): DayKey {
  const cur = weekdayOfKey(today);
  let d = (wd - cur + 7) % 7;
  if (d === 0 && !includeToday) d = 7;
  return addDays(today, d);
}

/**
 * Polish relative date in free text -> day key, or null.
 * "jutro", "pojutrze", "w sobotę", "do weekendu" (Saturday), "na tydzień", "za 3 dni", "na kilka dni".
 */
export function parsePolishDate(input: string, today: DayKey): DayKey | null {
  const t = normalizeText(input);
  const iso = /(\d{4}-\d{2}-\d{2})/.exec(input);
  if (iso && isDayKey(iso[1])) return iso[1]!;
  const dm = /\b(\d{1,2})\.(\d{1,2})\b/.exec(input);
  if (dm) {
    const year = Number(today.slice(0, 4));
    const k = `${year}-${dm[2]!.padStart(2, "0")}-${dm[1]!.padStart(2, "0")}`;
    if (isDayKey(k)) return k < today ? `${year + 1}${k.slice(4)}` : k;
  }
  if (/\bpojutrze\b/.test(t)) return addDays(today, 2);
  if (/\bjutr/.test(t)) return addDays(today, 1);
  if (/(^| )(dziś|dzis|dzisiaj)( |$)/.test(t)) return today;
  if (/weekend/.test(t)) return nextWeekday(today, 6);
  if (/\btydzie|\btygodni/.test(t)) {
    const m = /\b(\d+|\p{L}+)\s+tygodn/u.exec(t);
    const n = m ? Number(m[1]) || NUMBER_WORDS[m[1]!] || 1 : 1;
    return addDays(today, 7 * n);
  }
  const days = /\b(?:za|na)\s+(\d+|\p{L}+)\s+dn/u.exec(t);
  if (days) {
    const n = Number(days[1]) || NUMBER_WORDS[days[1]!];
    if (n) return addDays(today, n);
  }
  if (/\b(?:na|za) (?:dzień|dzien)( |$)/.test(t)) return addDays(today, 1);
  for (const [re, wd] of WEEKDAYS) if (re.test(t)) return nextWeekday(today, wd);
  return null;
}

/** "o 19", "o 19:30", "19.30", "o siódmej" -> minutes, or null. */
export function parsePolishTime(input: string): number | null {
  const t = input.toLowerCase();
  const hit =
    /\b(?:o|na|około|okolo)\s+(\d{1,2})(?:[:.](\d{2}))?\b/.exec(t) ?? /\b(\d{1,2})[:.](\d{2})\b/.exec(t);
  if (hit) {
    const h = Number(hit[1]);
    const min = Number(hit[2] ?? 0);
    if (h <= 23 && min <= 59) return h * 60 + min;
  }
  const words: [RegExp, number][] = [
    [/\bo (siódmej|siodmej)/, 19],
    [/\bo (ósmej|osmej)/, 20],
    [/\bo (szóstej|szostej)/, 18],
    [/\bo (piątej|piatej)/, 17],
    [/\bo (dziewiątej|dziewiatej)/, 21],
    [/\bo (dziesiątej|dziesiatej)/, 10],
    [/\bw (południe|poludnie)/, 12],
  ];
  for (const [re, h] of words) if (re.test(t)) return h * 60;
  return null;
}

/** Normalise a model-provided time string ("19", "19:00", "wieczorem"...). */
export function parseAt(at: string): {
  minutes: number | null;
  date: DayKey | null;
  part: "morning" | "afternoon" | "evening" | null;
} {
  const s = at.trim().toLowerCase();
  const iso = /^(\d{4}-\d{2}-\d{2})[t ](\d{1,2}):(\d{2})/.exec(s);
  if (iso && isDayKey(iso[1]))
    return { minutes: Number(iso[2]) * 60 + Number(iso[3]), date: iso[1]!, part: null };
  const hm = parseHm(s.replace(/^o\s+/, "").replace(".", ":"));
  if (hm !== null) return { minutes: hm, date: null, part: null };
  const part = /wiecz/.test(s)
    ? "evening"
    : /rano|ranem|morning/.test(s)
      ? "morning"
      : /południ|poludni|afternoon/.test(s)
        ? "afternoon"
        : null;
  const minutes = parsePolishTime(s);
  return { minutes, date: null, part };
}
